import { ArrowPathIcon, PauseIcon, PlayIcon } from "@heroicons/react/24/outline";
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { frameDurationSeconds } from "@/components/audits/replay/constants";
import FrameIssueOverlay from "@/components/audits/replay/FrameIssueOverlay";
import { objectContainCursorPosition, stickyCursorCenter } from "@/components/audits/replay/helpers";
import LiveEvaluationBrowserChrome from "@/components/domains/LiveEvaluationBrowserChrome";
import LiveEvaluationCursor from "@/components/domains/LiveEvaluationCursor";
import LiveEvaluationPlayerImage from "@/components/domains/LiveEvaluationPlayerImage";
import LiveEvaluationReplayControls from "@/components/domains/LiveEvaluationReplayControls";
import {
  playbackFrameVisible,
  playerFrameNeedsPaint,
  shouldAdvancePlaybackClock,
  showPlayerLoadingChrome,
} from "@/components/domains/playbackClock";
import { prefetchPlayerImages } from "@/components/domains/playerPrefetchUrls";
import { LANDING_WALKTHROUGH_SEEK_EVENT, type LandingDemoReplay } from "@/components/marketing/HeroDemoPlayer";
import type { Finding } from "@/types";

type Props = {
  replay: LandingDemoReplay;
};

const DEFAULT_IMAGE_ASPECT = 1440 / 1024;
const WALKTHROUGH_BALLOON_DOCK = { "landing-demo-finding-2": "below" } as const;

function replayEndTime(screenshotCount: number) {
  return Math.max(screenshotCount - 1, 0) * frameDurationSeconds;
}

function frameAtTime(time: number, screenshotCount: number) {
  if (screenshotCount <= 0) {
    return 0;
  }

  return Math.min(Math.floor(time / frameDurationSeconds), screenshotCount - 1);
}

function frameStep(replay: LandingDemoReplay, frameIndex: number) {
  const screenshot = replay.screenshots[frameIndex];
  return replay.steps.find((step) => step.screenshot_id === screenshot?.id) || replay.steps[frameIndex] || null;
}

export default function LandingWalkthroughPlayer({ replay }: Props) {
  const [mounted, setMounted] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [playing, setPlaying] = useState(false);
  const currentTimeRef = useRef(0);
  const playingRef = useRef(false);
  const playerRef = useRef<HTMLDivElement | null>(null);
  const [frameSize, setFrameSize] = useState({ width: 0, height: 0 });
  const [imageAspect, setImageAspect] = useState(DEFAULT_IMAGE_ASPECT);
  const [displayedFrame, setDisplayedFrame] = useState<{ id: string; url: string } | null>(null);

  useEffect(() => {
    setMounted(true);
  }, []);

  const setPlayhead = useCallback((time: number) => {
    currentTimeRef.current = time;
    setCurrentTime(time);
  }, []);

  const pausePlayback = useCallback(() => {
    playingRef.current = false;
    setPlaying(false);
  }, []);

  useEffect(() => {
    const seekFrame = (screenshotId: string | null) => {
      const index = screenshotId == null ? 0 : replay.screenshots.findIndex((frame) => frame.id === screenshotId);
      const frame = index < 0 ? 0 : index;
      setPlayhead(frame * frameDurationSeconds);
      playingRef.current = false;
      setPlaying(false);
    };

    const onSeek = (event: Event) => {
      seekFrame((event as CustomEvent<{ screenshotId: string | null }>).detail?.screenshotId ?? null);
    };

    const onHashChange = () => {
      if (window.location.hash === "#sample-evaluation") {
        seekFrame(null);
      }
    };

    window.addEventListener(LANDING_WALKTHROUGH_SEEK_EVENT, onSeek);
    window.addEventListener("hashchange", onHashChange);
    return () => {
      window.removeEventListener(LANDING_WALKTHROUGH_SEEK_EVENT, onSeek);
      window.removeEventListener("hashchange", onHashChange);
    };
  }, [replay.screenshots, setPlayhead]);

  const togglePlayback = () => {
    if (replay.screenshots.length === 0) {
      return;
    }

    if (playing) {
      pausePlayback();
      return;
    }

    const totalDuration = replayEndTime(replay.screenshots.length);
    if (currentTimeRef.current >= totalDuration) {
      setPlayhead(0);
      playingRef.current = replay.screenshots.length > 1;
      setPlaying(replay.screenshots.length > 1);
      return;
    }

    playingRef.current = true;
    setPlaying(true);
  };

  const frameIndex = frameAtTime(currentTime, replay.screenshots.length);
  const pendingScreenshot = replay.screenshots[frameIndex] ?? null;
  const pendingId = pendingScreenshot?.id ?? null;
  const pendingImageUrl = pendingScreenshot?.image_url ?? null;
  const frameVisible = playbackFrameVisible(displayedFrame?.id ?? null, pendingId);
  const showLoadingChrome = showPlayerLoadingChrome(false, frameVisible);

  useEffect(() => {
    prefetchPlayerImages(replay.screenshots.flatMap((frame) => (frame.image_url ? [frame.image_url] : [])));
  }, [replay.screenshots]);

  useLayoutEffect(() => {
    if (!playerFrameNeedsPaint(displayedFrame, pendingId, pendingImageUrl) || !pendingId || !pendingImageUrl) {
      return;
    }

    let cancelled = false;
    const image = new Image();
    const commit = () => {
      if (cancelled || image.naturalWidth <= 0) {
        return;
      }

      setImageAspect(image.naturalWidth / image.naturalHeight);
      setDisplayedFrame({ id: pendingId, url: pendingImageUrl });
    };

    image.onload = commit;
    image.src = pendingImageUrl;
    if (image.complete) {
      commit();
    }

    return () => {
      cancelled = true;
    };
  }, [displayedFrame, pendingId, pendingImageUrl]);

  useEffect(() => {
    if (!shouldAdvancePlaybackClock(playing, frameVisible)) {
      if (!playing) {
        playingRef.current = false;
      }
      return;
    }

    const totalDuration = replayEndTime(replay.screenshots.length);
    if (totalDuration <= 0 || currentTimeRef.current >= totalDuration) {
      playingRef.current = false;
      setPlaying(false);
      return;
    }

    let last = performance.now();
    let rafId = 0;

    const tick = (now: number) => {
      if (!playingRef.current) {
        return;
      }

      const next = Math.min(currentTimeRef.current + (now - last) / 1000, totalDuration);
      last = now;
      setPlayhead(next);
      if (next >= totalDuration) {
        playingRef.current = false;
        setPlaying(false);
        return;
      }

      rafId = window.requestAnimationFrame(tick);
    };

    rafId = window.requestAnimationFrame(tick);
    return () => window.cancelAnimationFrame(rafId);
  }, [playing, frameVisible, replay.screenshots.length, setPlayhead]);

  useLayoutEffect(() => {
    if (!mounted) {
      return;
    }

    const player = playerRef.current;
    if (!player || replay.screenshots.length === 0) {
      setFrameSize({ width: 0, height: 0 });
      return;
    }

    const syncSize = () => {
      const { width, height } = player.getBoundingClientRect();
      setFrameSize({ width, height });
    };
    syncSize();

    const observer = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect;
      setFrameSize({ width, height });
    });
    observer.observe(player);
    return () => observer.disconnect();
  }, [mounted, replay.screenshots.length]);

  const displayedScreenshot = replay.screenshots.find((frame) => frame.id === displayedFrame?.id) ?? null;
  const displayedIndex = displayedScreenshot
    ? replay.screenshots.findIndex((frame) => frame.id === displayedScreenshot.id)
    : frameIndex;
  const overlayScreenshot = displayedScreenshot;
  const overlayIndex = displayedScreenshot ? displayedIndex : frameIndex;
  const step = overlayScreenshot ? frameStep(replay, overlayIndex) : null;
  const cursorCenter = overlayScreenshot ? stickyCursorCenter(replay.steps, replay.screenshots, overlayIndex) : null;
  const cursor = cursorCenter
    ? objectContainCursorPosition({
        containerWidth: frameSize.width,
        containerHeight: frameSize.height,
        imageAspect,
        x: cursorCenter.x,
        y: cursorCenter.y,
      })
    : null;
  const showCursorPing =
    Boolean(overlayScreenshot) && playing && (step?.action_type === "click" || step?.action_type === "fill");
  const pageUrl = overlayScreenshot?.url || pendingScreenshot?.url || "";
  const pageTitle = overlayScreenshot?.title || pendingScreenshot?.title || "Browsing";
  const findingsByScreenshot = replay.findings.reduce<Record<string, Finding[]>>((grouped, finding) => {
    if (!finding.screenshot_id) return grouped;

    grouped[finding.screenshot_id] = [...(grouped[finding.screenshot_id] || []), finding];
    return grouped;
  }, {});
  const currentFrameFindings = overlayScreenshot ? findingsByScreenshot[overlayScreenshot.id] || [] : [];
  const findingMarks = replay.screenshots.map((frame) => ({
    id: frame.id,
    count: (findingsByScreenshot[frame.id] || []).length,
  }));
  const atEnd = replay.screenshots.length > 1 && !playing && currentTime >= replayEndTime(replay.screenshots.length);

  if (!mounted) {
    return <div className="aspect-[1440/1024] w-full rounded-lg border border-gray-200 bg-slate-50" />;
  }

  return (
    <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
      <LiveEvaluationBrowserChrome url={pageUrl} />
      <div
        ref={playerRef}
        className="group relative aspect-[1440/1024] w-full overflow-hidden bg-slate-50"
        aria-busy={showLoadingChrome || undefined}
      >
        {displayedFrame?.url ? (
          <LiveEvaluationPlayerImage
            src={displayedFrame.url}
            alt={displayedScreenshot?.title || pageTitle}
            dimmed={showLoadingChrome}
          />
        ) : null}
        {showLoadingChrome ? (
          <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center">
            <div
              className="h-10 w-10 animate-spin rounded-full border-2 border-white/80 border-t-primary shadow-sm"
              aria-hidden="true"
            />
          </div>
        ) : null}
        {cursor ? (
          <LiveEvaluationCursor x={cursor.x} y={cursor.y} viewport={replay.viewport} ping={showCursorPing} />
        ) : null}
        {overlayScreenshot ? (
          <FrameIssueOverlay
            findings={currentFrameFindings}
            frameId={overlayScreenshot.id}
            frameSize={frameSize}
            imageAspect={imageAspect}
            compactDetail
            balloonDockById={WALKTHROUGH_BALLOON_DOCK}
            onOpenFinding={pausePlayback}
          />
        ) : null}
        <button
          type="button"
          className={`absolute left-1/2 top-1/2 z-20 flex h-16 w-16 -translate-x-1/2 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full bg-black/60 text-white shadow-lg backdrop-blur-sm transition-all hover:scale-105 hover:bg-black/75 hover:opacity-100 focus-visible:opacity-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-white ${
            playing
              ? "opacity-0 group-hover:opacity-20 group-focus-within:opacity-20"
              : "opacity-0 group-hover:opacity-100 group-focus-within:opacity-100"
          }`}
          onClick={togglePlayback}
          aria-label={playing ? "Pause replay" : atEnd ? "Restart replay" : "Play replay"}
        >
          {playing ? (
            <PauseIcon className="h-8 w-8" aria-hidden="true" />
          ) : atEnd ? (
            <ArrowPathIcon className="h-8 w-8" aria-hidden="true" />
          ) : (
            <PlayIcon className="ml-1 h-8 w-8" aria-hidden="true" />
          )}
        </button>
      </div>
      <LiveEvaluationReplayControls
        screenshotCount={replay.screenshots.length}
        currentTime={currentTime}
        playing={playing}
        findingMarks={findingMarks}
        onSeek={(time, options) => {
          setPlayhead(time);
          playingRef.current = options?.play ?? false;
          setPlaying(options?.play ?? false);
        }}
        onTogglePlayback={togglePlayback}
      />
    </div>
  );
}
